/**
 * =============================================================================
 * LICENSE CONTEXT — App-wide license gate
 * =============================================================================
 * Placed early in the provider tree (before RequireAuthenticated).
 * If the license is invalid the app renders LicenseActivationPage instead
 * of the normal route tree.
 *
 * Verification sequence on mount:
 *  1. Read cache  → valid & not expired? allow immediately.
 *  2. Read stored license key from localStorage.
 *  3. If key exists: call LicenseService.verifyOnline().
 *  4. If network unavailable and cache exists: allow with stale cache.
 *  5. If nothing valid → show LicenseActivationPage.
 */

import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import { LicenseService } from '../services/licenseService';
import { getMachineFingerprint } from '../utils/machineFingerprint';
import type { CachedLicenseState, LicenseContextValue } from '../types/license';

// ─── Storage keys ─────────────────────────────────────────────────────────────

const KEY_STORAGE_KEY = 'wf_license_key';

// ─── Context ──────────────────────────────────────────────────────────────────

const LicenseContext = createContext<LicenseContextValue | null>(null);

export function useLicense(): LicenseContextValue {
  const ctx = useContext(LicenseContext);
  if (!ctx) {
    throw new Error('useLicense must be used inside LicenseProvider');
  }
  return ctx;
}

// ─── Provider ─────────────────────────────────────────────────────────────────

interface LicenseProviderProps {
  children: React.ReactNode;
  /** Rendered when the license is not valid and not currently checking */
  fallback?: React.ReactNode;
}

export function LicenseProvider({ children, fallback }: LicenseProviderProps) {
  const [licenseKey, setLicenseKeyState] = useState<string>(() => {
    try {
      return localStorage.getItem(KEY_STORAGE_KEY) ?? '';
    } catch {
      return '';
    }
  });

  const [licenseState, setLicenseState] = useState<CachedLicenseState | null>(
    () => LicenseService.getCachedState(),
  );

  const [isChecking, setIsChecking] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Resolved machine fingerprint (cached after first call)
  const machineIdRef = useRef<string | null>(null);

  async function getMachineId(): Promise<string> {
    if (machineIdRef.current) return machineIdRef.current;
    const id = await getMachineFingerprint();
    machineIdRef.current = id;
    return id;
  }

  // ── Persist license key ─────────────────────────────────────────────────────

  const setLicenseKey = useCallback((key: string) => {
    setLicenseKeyState(key);
    try {
      if (key) {
        localStorage.setItem(KEY_STORAGE_KEY, key);
      } else {
        localStorage.removeItem(KEY_STORAGE_KEY);
      }
    } catch {
      // ignore
    }
    setError(null);
  }, []);

  // ── Online verification ─────────────────────────────────────────────────────

  const verify = useCallback(async () => {
    const key = licenseKey.trim().toUpperCase();
    if (!key) {
      setError('Please enter your license key.');
      return;
    }

    setIsChecking(true);
    setError(null);

    const machineId = await getMachineId();
    const version = import.meta.env.VITE_APP_VERSION ?? '1.0.0';

    const result = await LicenseService.verifyOnline(key, machineId, version);

    setIsChecking(false);

    if (result.valid) {
      setLicenseState(LicenseService.getCachedState());
      setError(null);
    } else {
      setLicenseState(null);
      setError(result.message ?? 'License verification failed.');
    }
  }, [licenseKey]);

  // ── Offline activation ──────────────────────────────────────────────────────

  const activateOffline = useCallback(
    async (token: string): Promise<{ success: boolean; message: string }> => {
      const machineId = await getMachineId();
      const result = await LicenseService.activateOffline(token, machineId);
      if (result.success) {
        setLicenseState(LicenseService.getCachedState());
        setError(null);
      }
      return result;
    },
    [],
  );

  // ── Clear license ───────────────────────────────────────────────────────────

  const clearLicense = useCallback(() => {
    LicenseService.clearCache();
    setLicenseState(null);
    setError(null);
  }, []);

  // ── Startup verification ────────────────────────────────────────────────────

  useEffect(() => {
    const cached = LicenseService.getCachedState();

    // Already valid in cache → nothing to do
    if (cached?.valid) {
      setLicenseState(cached);
      return;
    }

    // No key stored → user must activate
    const storedKey = licenseKey.trim();
    if (!storedKey) return;

    // Key present but no valid cache → verify online
    (async () => {
      setIsChecking(true);
      try {
        const machineId = await getMachineId();
        const version = import.meta.env.VITE_APP_VERSION ?? '1.0.0';
        const result = await LicenseService.verifyOnline(storedKey.toUpperCase(), machineId, version);
        if (result.valid) {
          setLicenseState(LicenseService.getCachedState());
        } else {
          setLicenseState(null);
          setError(result.message ?? 'License verification failed.');
        }
      } catch (err: any) {
        // Network error — if there is a stale cache (valid=false means it was
        // written by a previous failure) we still block. But if we had a
        // previously-valid offline state that is now technically "expired" by
        // TTL, allow access until connectivity is restored.
        const stale = LicenseService.getCachedState();
        if (stale?.valid) {
          setLicenseState(stale);
        } else {
          setError('Unable to verify license — check your network connection.');
        }
      } finally {
        setIsChecking(false);
      }
    })();
  // Run once on mount
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Derived flag ────────────────────────────────────────────────────────────

  const isLicenseValid = licenseState?.valid === true;

  // ── Context value ───────────────────────────────────────────────────────────

  const value: LicenseContextValue = {
    isChecking,
    isLicenseValid,
    licenseState,
    licenseKey,
    error,
    setLicenseKey,
    verify,
    activateOffline,
    clearLicense,
  };

  return (
    <LicenseContext.Provider value={value}>
      {/* Gate: render fallback (activation screen) until license is confirmed */}
      {isLicenseValid || isChecking ? children : (fallback ?? null)}
    </LicenseContext.Provider>
  );
}
